import type { ReactNode } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { createContext, useCallback, useMemo } from 'react';
import React from 'react';
import type { PaintProduct, RGB } from '@modules/paint-picker/type';
import { PaintPicker } from '../component/paint-picker/PaintPicker';
import type { PaintPickerAction } from './PaintPickerReducer';
import { paintPickerReducer } from './PaintPickerReducer';
import { useLatest, useSessionStorageState } from 'ahooks';
import { useTracker } from '@archipro-website/tracker';
import { PALETTE_COLOUR_LIMIT } from '../config';
import { useFetcher } from '@remix-run/react';
import type { PaintColour, PaintColourProduct } from '~/modules/project/api';
import { QUERY_PAINT_COLOUR_PRODUCT } from '~/routes/remix-api.paint-picker.get-paint-colour-product';
import { QUERY_PAINT_COLOURS } from '~/routes/remix-api.paint-picker.get-paint-colours';
import nearestColorLib from 'nearest-color';

interface PaintPickerContextProviderProps {
    children: ReactNode;
    containerRef?: React.RefObject<HTMLDivElement>;
    paintPickerActive?: boolean;
    bannerUrl?: string;
    projectId?: number;
    projectLink?: string;
    isDesktop?: boolean;
    isEnabled?: boolean;
}

export interface PaintPickerState {
    paintPickerActive: boolean;
    populateDefaults: boolean;
    previewPaint: PaintProduct | null;
    colourCache: Map<string, PaintProduct>;
    touchEnabled: boolean;
    activeImageSrc?: string;
    usageHintEnabled?: boolean;
    isPicking: boolean;
    isEnabled: boolean;
}

const PAINT_PICKER_SESSION_KEY = 'paint-picker-session';

const PaintPickerContext = createContext<
    | {
          state: PaintPickerState;
          dispatch: (action: PaintPickerAction) => void;
          paintSelection: PaintProduct[];
          matchRgbToReseneColour: (rgb: RGB) => PaintProduct | null;
          openPaintPicker: () => void;
          onSelectPreview: (
              paint: PaintProduct | null,
              fetchProductInfo?: boolean
          ) => void;
          closePaintPicker: () => void;
          setPickingProcess: (active: boolean) => void; // added to match interface with new picker, does nothing
          setUsageHint: (active: boolean) => void; // added to match interface with new picker, does nothing
          addPaintToSelection: (paint: PaintProduct) => void;
          updatePaintSelection: (
              paint: PaintProduct[],
              populateDefaults?: boolean
          ) => void;
          removePaint: (id: number) => void;
          availablePaints: PaintProduct[];
      }
    | undefined
>(undefined);

export const useFFPaintPickerContext = (newResenePickerEnabled: boolean) => {
    const context = React.useContext(PaintPickerContext);

    if (!context && !newResenePickerEnabled) {
        throw new Error('Cannot be used outside PaintPickerContext');
    }

    return context;
};

export const usePaintPickerContext = () => {
    const context = React.useContext(PaintPickerContext);

    if (!context) {
        throw new Error('Cannot be used outside PaintPickerContext');
    }

    return context;
};

const createPaintFormData = (paints: PaintProduct[]) => {
    const formData = new FormData();
    for (const paint of paints) {
        if (paint.link) {
            continue;
        }
        formData.append('id', paint.productId.toString());
    }

    return formData;
};

export const PaintPickerContextProvider = ({
    children,
    projectLink,
    bannerUrl,
}: PaintPickerContextProviderProps) => {
    const [state, dispatch] = React.useReducer(paintPickerReducer, {
        paintPickerActive: false,
        populateDefaults: true,
        previewPaint: null,
        colourCache: new Map(),
        touchEnabled: false,
        activeImageSrc: undefined,
        usageHintEnabled: false,
        isPicking: false,
        isEnabled: true,
    });

    const [paintSelection, setPaintSelection] = useSessionStorageState<
        PaintProduct[]
    >(PAINT_PICKER_SESSION_KEY, {
        defaultValue: [],
    });

    const [availablePaints, setAvailablePaints] = useState<PaintProduct[]>([]);

    const tracker = useTracker();

    const fetcher = useFetcher<PaintColourProduct[]>();
    const colourFetcher = useFetcher<PaintColour[]>();

    const locationFetcher = useLatest(colourFetcher);

    useEffect(() => {
        if (projectLink) {
            locationFetcher.current.submit(
                { link: projectLink },
                {
                    method: 'get',
                    action: QUERY_PAINT_COLOURS,
                    preventScrollReset: true,
                }
            );
        } else {
            setAvailablePaints((availablePaints) => {
                if (availablePaints.length === 0) {
                    return availablePaints;
                }
                return [];
            });
        }
    }, [locationFetcher, projectLink]);

    useEffect(() => {
        if (colourFetcher.state !== 'idle') return;
        const newColours =
            colourFetcher.data?.map((colour) => ({
                id: colour.ID,
                title: colour.Title,
                code: colour.Code,
                hex: colour.Hex,
                productId: colour.ProductID,
                rgb: { r: colour.R, g: colour.G, b: colour.B },
                userSelected: false,
            })) ?? [];
        setAvailablePaints(newColours);
    }, [colourFetcher.state, colourFetcher.data]);

    const nearestColor = useMemo(() => {
        return nearestColorLib.from(
            availablePaints.map((paint) => {
                return paint.rgb;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any
        );
    }, [availablePaints]);

    useEffect(() => {
        if (!fetcher.data) return;
        const paintColourProducts = fetcher.data;

        // desktop selection
        setPaintSelection((state) => {
            if (!state) {
                return [];
            }

            return state.map((paint) => {
                const returnedPaint = paintColourProducts.find(
                    (paintColourProduct) =>
                        paintColourProduct.ID === paint.productId
                );
                if (returnedPaint) {
                    return {
                        ...paint,
                        link: returnedPaint?.Link,
                    };
                } else {
                    return paint;
                }
            });
        });

        //mobile selection
        dispatch({
            type: 'SET_PREVIEW_PAINT_MOBILE',
            payload: paintColourProducts,
        });
    }, [fetcher.data, setPaintSelection]);

    const fetchPaintColourProduct = useCallback(
        (paints: PaintProduct[]) => {
            const formData = new FormData();
            paints.forEach((paint) => {
                if (!paint.link) {
                    formData.append('id', paint.productId.toString());
                }
            });
            if (!formData.getAll('id').length) return;
            fetcher.submit(formData, {
                method: 'get',
                action: QUERY_PAINT_COLOUR_PRODUCT,
                preventScrollReset: true,
            });
        },
        [fetcher]
    );

    const matchRgbToReseneColour = useCallback(
        (rgb: RGB): PaintProduct | null => {
            const rgbStringify: string = `${rgb.r}-${rgb.g}-${rgb.b}`;

            if (availablePaints.length === 0) return null;

            if (state.colourCache.has(rgbStringify)) {
                return state.colourCache.get(rgbStringify) || null;
            }

            const nearestReseneHex = nearestColor(rgb)?.toLowerCase();

            const paintFound = availablePaints.find(
                (x) => x.hex.toLowerCase() === nearestReseneHex
            );

            paintFound &&
                dispatch({
                    type: 'SET_COLOUR_CACHE',
                    payload: state.colourCache.set(rgbStringify, paintFound),
                });

            return paintFound || null;
        },
        [availablePaints, state.colourCache, dispatch, nearestColor]
    );

    const openPaintPicker = () => {
        tracker.log('Resene-DetailsPage-Photo-Enabled', {
            url: new URL(window.location.href),
        });
        dispatch({ type: 'TOGGLE_PAINT_PICKER', payload: true });
    };

    const onSelectPreview = useCallback(
        (paint: PaintProduct | null, fetchProductInfo?: boolean) => {
            dispatch({ type: 'SET_PREVIEW_PAINT', payload: paint });
            if (paint && fetchProductInfo) {
                const formData = createPaintFormData([paint]);
                const hasProductsToFetch =
                    Array.from(formData.values()).length > 0;
                if (hasProductsToFetch) {
                    fetcher.submit(formData, {
                        method: 'get',
                        action: QUERY_PAINT_COLOUR_PRODUCT,
                        preventScrollReset: true,
                    });
                }
            }
        },
        [fetcher]
    );

    const closePaintPicker = () => {
        tracker.log('Resene-DetailsPage-Photo-Disabled', {
            url: new URL(window.location.href),
        });
        dispatch({ type: 'TOGGLE_PAINT_PICKER', payload: false });

        // for the pretty animation
        setTimeout(() => {
            updatePaintSelection([], true);
        }, 200);
    };

    const addPaintToSelection = (paint: PaintProduct) => {
        // any interaction with the selection means the user claims the selection as their own selection.
        paintSelection.forEach((p) => (p.userSelected = true));
        paint.userSelected = true;

        const newState = [
            ...paintSelection.filter((i) => i.id != paint.id), // Remove any existing paint with the same id
            paint,
        ].slice(-PALETTE_COLOUR_LIMIT); // Keep only the last {limit} paints, including the new one

        setPaintSelection(newState);
        fetchPaintColourProduct(newState);
        dispatch({ type: 'TOGGLE_POPULATE_DEFAULT', payload: false });

        tracker.log('Resene-Colour-Added-Palette', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: {
                ExtraData: paint.title,
            },
        });
    };

    const updatePaintSelection = (
        paints: PaintProduct[],
        populateDefaults = false
    ) => {
        // Only fully update selection if the selection contains no user selected paints
        if (!paintSelection.find((p) => p.userSelected)) {
            setPaintSelection(paints);
            fetchPaintColourProduct(paints);
            dispatch({
                type: 'TOGGLE_POPULATE_DEFAULT',
                payload: populateDefaults,
            });
        }
    };

    const removePaint = (id: number) => {
        if (paintSelection.length > 0) {
            const newState = paintSelection.filter((p) => p.id !== id);
            // any interaction with the selection means the user claims the selection as their own selection.
            newState.forEach((p) => (p.userSelected = true));
            setPaintSelection(newState);
            dispatch({
                type: 'TOGGLE_POPULATE_DEFAULT',
                payload: false,
            });
        }
    };

    return (
        <PaintPickerContext.Provider
            value={{
                state,
                dispatch,
                paintSelection,
                matchRgbToReseneColour,
                openPaintPicker,
                closePaintPicker,
                setPickingProcess: () => {},
                setUsageHint: () => {},
                onSelectPreview,
                addPaintToSelection,
                updatePaintSelection,
                removePaint,
                availablePaints,
            }}
        >
            {children}
            {availablePaints.length !== 0 && (
                <PaintPicker bannerUrl={bannerUrl} />
            )}
        </PaintPickerContext.Provider>
    );
};
